import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle } from "lucide-react";
import { authAPI, type User } from "../api/authApi";
import { updateFileVisibility, type FileVisibility } from "../api/file";
import { resolveLabwickUploadUrl } from "../utils/pictureUtils";

interface FileSharingPromptProps {
  /** The uploaded file's id; the prompt patches its sharing when confirmed. */
  fileId: string | null;
  /** Stored filename/path of the just-uploaded file (used for display + public URL). */
  filename?: string;
  open: boolean;
  /** Called once the choice is applied (or skipped); the caller then continues its flow. */
  onResolved: () => void;
}

/**
 * Shown after a successful upload when the uploader belongs to ≥1 ACL group.
 * Lets them keep the file private or share it with a group, and toggle public access.
 * The choice is always saved as the user's account default for future uploads.
 */
export function FileSharingPrompt({ fileId, filename, open, onResolved }: FileSharingPromptProps) {
  const [groups, setGroups] = useState<Array<{ uid: string; title: string }>>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [visibility, setVisibility] = useState<FileVisibility>("private");
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [isPublic, setIsPublic] = useState(true);
  const [saving, setSaving] = useState(false);

  const displayName = filename ? filename.split("/").pop() : undefined;
  const publicUrl = filename ? resolveLabwickUploadUrl(filename) : "";

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingGroups(true);
    // Seed from the user's remembered defaults, then load their groups.
    const stored = (() => {
      try {
        return JSON.parse(localStorage.getItem("user") || "null") as User | null;
      } catch {
        return null;
      }
    })();
    if (stored?.fileVisibilityDefault) setVisibility(stored.fileVisibilityDefault);
    if (stored?.fileShareGroupDefault) setSelectedGroups([stored.fileShareGroupDefault]);
    if (typeof stored?.filePublicDefault === "boolean") setIsPublic(stored.filePublicDefault);

    authAPI.getMyGroups().then((g) => {
      if (cancelled) return;
      setGroups(g);
      setLoadingGroups(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const toggleGroup = (uid: string, checked: boolean) => {
    setSelectedGroups((prev) =>
      checked ? [...new Set([...prev, uid])] : prev.filter((g) => g !== uid)
    );
  };

  const effectiveVisibility: FileVisibility =
    visibility === "shared" && selectedGroups.length > 0 ? "shared" : "private";

  const handleConfirm = async () => {
    if (!fileId) {
      onResolved();
      return;
    }
    setSaving(true);
    try {
      const shareGroups = effectiveVisibility === "shared" ? selectedGroups : [];
      const sharing = {
        visibility: effectiveVisibility,
        groups: shareGroups,
        group: shareGroups[0] ?? null,
        public: isPublic,
      };
      await updateFileVisibility(fileId, sharing);
      // Always remember the choice as the user's default for future uploads.
      await authAPI.updateFileSharingDefaults({
        fileVisibilityDefault: sharing.visibility,
        fileShareGroupDefault: sharing.group,
        filePublicDefault: sharing.public,
      });
    } catch (error) {
      console.error("Failed to apply file sharing:", error);
    } finally {
      setSaving(false);
      onResolved();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onResolved(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share this file?</DialogTitle>
          <DialogDescription>
            Choose who can see {displayName ? <span className="font-medium">{displayName}</span> : "this file"} within Labwick.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Visibility in Labwick</Label>
            <Select value={visibility} onValueChange={(v: FileVisibility) => setVisibility(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="private">Keep private (only me)</SelectItem>
                <SelectItem value="shared" disabled={!loadingGroups && groups.length === 0}>
                  Share with a group
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {visibility === "shared" && (
            <div className="space-y-2">
              <Label>Groups</Label>
              {loadingGroups ? (
                <p className="text-sm text-muted-foreground">Loading groups…</p>
              ) : groups.length === 0 ? (
                <p className="text-sm text-muted-foreground">No groups available.</p>
              ) : (
                <div className="space-y-2 rounded-md border p-3">
                  {groups.map((g) => (
                    <label key={g.uid} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={selectedGroups.includes(g.uid)}
                        onCheckedChange={(c) => toggleGroup(g.uid, c === true)}
                      />
                      <span className="text-sm">{g.title}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={isPublic}
                onCheckedChange={(c) => setIsPublic(c === true)}
              />
              <span className="text-sm font-medium">Publicly accessible</span>
            </label>
            {isPublic ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/60 dark:bg-amber-950/40">
                <p className="flex items-start gap-1 text-xs text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    Anyone with the link can open this file
                    {publicUrl ? (
                      <>
                        {" "}
                        at{" "}
                        <a
                          href={publicUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="break-all font-mono text-blue-600 hover:underline dark:text-blue-400"
                        >
                          {publicUrl}
                        </a>
                      </>
                    ) : null}{" "}
                    even without signing in.
                  </span>
                </p>
              </div>
            ) : null}
          </div>

          <p className="text-xs text-muted-foreground">
            Your choice is saved as the default for future uploads.
          </p>
        </div>

        <DialogFooter>
          <Button
            onClick={handleConfirm}
            disabled={saving || (visibility === "shared" && selectedGroups.length === 0)}
          >
            {saving ? "Saving…" : "Done"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default FileSharingPrompt;
